
import React, { useState, useEffect, useRef } from 'react';
import { Task, FocusSettings, UserProfile, TimerMode, TimerState } from '../types';
import { Button } from '../components/Button';
import { Play, Pause, RotateCcw, CheckCircle, ArrowLeft, Coffee, Zap, Volume2, VolumeX, CloudRain, Trees, Waves, Shield, Lock, Globe } from 'lucide-react';
import { SOUNDSCAPES } from '../constants';
import { recordFocusSession } from '../services/firebaseService';
import { startTimer, pauseTimer, resetTimerService, getTimerState, syncTimerState } from '../services/timerService';

// Declare chrome API
declare const chrome: any;
const isExtensionEnvironment = () => {
  return (
    typeof chrome !== 'undefined' &&
    typeof chrome.runtime !== 'undefined' &&
    typeof chrome.runtime.id === 'string'
  );
};

interface FocusViewProps {
  task: Task;
  user: UserProfile; 
  onComplete: (taskId: string) => void;
  onExit: () => void;
  onLogTime: (taskId: string, seconds: number) => void;
}

const SoundIcon = ({ name, size = 18 }: { name: string, size?: number }) => {
  switch(name) {
    case 'CloudRain': return <CloudRain size={size} />;
    case 'Trees': return <Trees size={size} />;
    case 'Coffee': return <Coffee size={size} />;
    case 'Waves': return <Waves size={size} />;
    case 'Zap': return <Zap size={size} />;
    case 'Globe': return <Globe size={size} />;
    default: return <Volume2 size={size} />;
  }
};

export const FocusView: React.FC<FocusViewProps> = ({ task, user, onComplete, onExit, onLogTime }) => {
  const settings = user.focusSettings;
  const config = settings || { workDuration: 25, shortBreakDuration: 5, longBreakDuration: 15, blockedSites: [] };

  const [timerState, setTimerState] = useState<TimerState>({
    status: 'idle',
    mode: 'work',
    endTime: null,
    remainingSeconds: config.workDuration * 60,
    totalDuration: config.workDuration * 60,
    taskId: task.id,
    accumulatedTime: 0,
    lastUpdated: Date.now()
  });

  const [hasAwardedSessionXp, setHasAwardedSessionXp] = useState(false);
  const [selectedSoundId, setSelectedSoundId] = useState<string | null>(null);
  const [volume, setVolume] = useState(0.5);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const RADIUS = 115;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  useEffect(() => {
    const init = async () => {
      const persisted = await getTimerState();
      if (persisted.status === 'idle') {
           await resetTimerService(config.workDuration * 60, 'work');
      }
      const synced = await syncTimerState();
      setTimerState(synced);
    };
    init();

    const interval = setInterval(async () => {
      const current = await syncTimerState();
      setTimerState(current);

      if (current.status === 'finished' && !hasAwardedSessionXp) {
         if (current.mode === 'work') {
             recordFocusSession(user.uid);
             setHasAwardedSessionXp(true);
         }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getDisplayTime = () => {
     if (timerState.status === 'running' && timerState.endTime) {
         const diff = Math.max(0, Math.ceil((timerState.endTime - Date.now()) / 1000));
         return diff;
     }
     return timerState.remainingSeconds;
  };

  const timeLeft = getDisplayTime();
  const isActive = timerState.status === 'running';

  const applyBlocking = (shouldBlock: boolean) => {
  if (!isExtensionEnvironment()) return;

  try {
    if (
      shouldBlock &&
      timerState.mode === 'work' &&
      config.blockedSites &&
      config.blockedSites.length > 0
    ) {
      chrome.runtime.sendMessage({
        type: 'START_FOCUS_BLOCKING',
        domains: config.blockedSites
      });
    } else {
      chrome.runtime.sendMessage({ type: 'STOP_FOCUS_BLOCKING' });
    }
  } catch (err) {
    console.warn("Extension messaging failed:", err);
  }
};

  
  useEffect(() => {
    applyBlocking(isActive && timerState.mode === 'work');
    return () => { applyBlocking(false); };
  }, [isActive, timerState.mode, config.blockedSites]);

  // Audio initialization
  useEffect(() => {
    const audio = new Audio();
    audio.loop = true;
    audioRef.current = audio;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Update Audio Source and state
  useEffect(() => {
    if (!audioRef.current) return;

    if (selectedSoundId) {
      const sound = SOUNDSCAPES.find(s => s.id === selectedSoundId);
      if (sound && audioRef.current.src !== sound.url) {
        audioRef.current.src = sound.url;
        audioRef.current.volume = volume;
        if (isActive) {
          audioRef.current.play().catch(() => {
            console.log("Audio needs user interaction to play.");
          });
        }
      }
    } else {
      audioRef.current.pause();
    }
  }, [selectedSoundId]);

  // Handle Play/Pause based on timer status
  useEffect(() => {
    if (!audioRef.current || !selectedSoundId) return;

    if (isActive) {
      audioRef.current.play().catch(() => {
        // Silently fail if blocked by browser policy, user will click something soon
      });
    } else {
      audioRef.current.pause();
    }
  }, [isActive, selectedSoundId]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const handleToggleTimer = async () => {
    if (isActive) {
      const newState = await pauseTimer();
      setTimerState(newState);
    } else {
      if (timerState.status === 'finished') {
         await handleModeSwitch(timerState.mode);
      }
      const newState = await startTimer(timerState.remainingSeconds, timerState.mode, task.id);
      setTimerState(newState);
      if (timerState.mode !== 'work') setHasAwardedSessionXp(false);

      // Explicitly try to play audio here since this is a direct click event
      if (audioRef.current && selectedSoundId) {
          audioRef.current.play().catch(e => console.warn("Direct play failed:", e));
      }
    }
  };
  
  const handleResetTimer = async () => {
    setHasAwardedSessionXp(false);
    let duration = config.workDuration * 60;
    if (timerState.mode === 'shortBreak') duration = config.shortBreakDuration * 60;
    if (timerState.mode === 'longBreak') duration = config.longBreakDuration * 60;

    const { newState, capturedTime } = await resetTimerService(duration, timerState.mode);
    setTimerState(newState);
    if (capturedTime > 0) onLogTime(task.id, Math.floor(capturedTime));
  };

  const handleModeSwitch = async (newMode: TimerMode) => {
    setHasAwardedSessionXp(false);
    let duration = config.workDuration * 60;
    if (newMode === 'shortBreak') duration = config.shortBreakDuration * 60;
    if (newMode === 'longBreak') duration = config.longBreakDuration * 60;

    const { newState, capturedTime } = await resetTimerService(duration, newMode);
    setTimerState(newState);
    if (capturedTime > 0) onLogTime(task.id, Math.floor(capturedTime));
  };

  const handleExitView = async () => {
    applyBlocking(false);
    if (audioRef.current) audioRef.current.pause();
    
    let accumulated = timerState.accumulatedTime;
    if (timerState.status === 'running' && timerState.endTime) {
        const now = Date.now();
        const end = timerState.endTime;
        const actualEnd = Math.min(now, end);
        accumulated += Math.max(0, (actualEnd - timerState.lastUpdated) / 1000);
    }
    
    if (accumulated > 0) onLogTime(task.id, Math.floor(accumulated));
    await pauseTimer();
    onExit();
  };

  const handleCompleteTask = async () => {
    applyBlocking(false);
    if (audioRef.current) audioRef.current.pause();
    
    let accumulated = timerState.accumulatedTime;
     if (timerState.status === 'running' && timerState.endTime) {
        const now = Date.now();
        const end = timerState.endTime;
        const actualEnd = Math.min(now, end);
        accumulated += Math.max(0, (actualEnd - timerState.lastUpdated) / 1000);
    }

    if (accumulated > 0) onLogTime(task.id, Math.floor(accumulated));
    await resetTimerService(config.workDuration * 60, 'work');
    onComplete(task.id);
  };

  const formatTime = (seconds: number) => {
    const s = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((timerState.totalDuration - timeLeft) / timerState.totalDuration) * 100;

  const getThemeColor = () => {
    if (timerState.mode === 'work') return 'text-blue-600';
    return 'text-emerald-500';
  };

  const handleSelectSound = (soundId: string) => {
      const isSelected = selectedSoundId === soundId;
      const newSoundId = isSelected ? null : soundId;
      setSelectedSoundId(newSoundId);
      
      // User interaction here allows audio to play
      if (newSoundId && audioRef.current) {
          // Setting source is handled in useEffect, but we can pre-trigger
          setTimeout(() => {
            if (audioRef.current && isActive) {
               audioRef.current.play().catch(e => console.log("Selection play blocked:", e));
            }
          }, 100);
      }
  };

  return (
    <div className="flex flex-col items-center justify-start min-h-full max-w-2xl mx-auto text-center px-4 animate-fade-in relative pb-10">
      <button 
        onClick={handleExitView}
        className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 flex items-center gap-2 text-sm z-10"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center gap-2 mt-4 mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg z-10">
        <button
          onClick={() => handleModeSwitch('work')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${timerState.mode === 'work' ? 'bg-white dark:bg-dark-surface text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Focus
        </button>
        <button
          onClick={() => handleModeSwitch('shortBreak')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${timerState.mode === 'shortBreak' ? 'bg-white dark:bg-dark-surface text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Short Break
        </button>
        <button
          onClick={() => handleModeSwitch('longBreak')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${timerState.mode === 'longBreak' ? 'bg-white dark:bg-dark-surface text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Long Break
        </button>
      </div>

      <div className="mb-6">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide mb-4 uppercase ${timerState.mode === 'work' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
          {timerState.mode === 'work' ? <Zap size={12}/> : <Coffee size={12}/>}
          {timerState.mode === 'work' ? 'Focus Mode' : 'Break Time'}
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight max-w-lg truncate px-2">
          {timerState.mode === 'work' ? task.title : 'Take a breather'}
        </h1>
        {timerState.mode === 'work' && isActive && config.blockedSites && config.blockedSites.length > 0 && (
           <p className="text-xs text-red-500 mt-2 font-medium flex items-center justify-center gap-1 animate-pulse">
             <Shield size={12} /> Deep Focus: {config.blockedSites.length} sites blocked
           </p>
        )}
      </div>

      <div className="relative w-64 h-64 mb-8 flex-shrink-0 flex items-center justify-center">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 256 256">
          <circle
            cx="128"
            cy="128"
            r={RADIUS}
            stroke="currentColor"
            strokeWidth="8"
            fill="transparent"
            className="text-gray-200 dark:text-gray-700"
          />
          <circle
            cx="128"
            cy="128"
            r={RADIUS}
            stroke="currentColor"
            strokeWidth="8"
            fill="transparent"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - progress / 100)}
            strokeLinecap="round"
            className={`${getThemeColor()} transition-all duration-1000 ease-linear ${isActive ? 'opacity-100' : 'opacity-80'}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-6xl font-mono font-bold text-gray-800 dark:text-white tracking-tighter">
            {formatTime(timeLeft)}
          </span>
          <span className="text-gray-400 text-sm mt-2 font-medium uppercase tracking-widest">
            {timerState.status === 'finished' ? 'Finished' : (isActive ? (timerState.mode === 'work' ? 'Focusing' : 'Resting') : 'Paused')}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-8">
        <Button 
          onClick={handleToggleTimer}
          className={`w-16 h-16 rounded-full flex items-center justify-center !p-0 ${timerState.mode === 'work' ? (isActive ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700') : (isActive ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-500 hover:bg-emerald-600')}`}
        >
          {isActive ? <Pause size={28} fill="currentColor" /> : <Play size={28} fill="currentColor" className="ml-1" />}
        </Button>
        
        <button 
          onClick={handleResetTimer}
          className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
          title="Reset Timer"
        >
          <RotateCcw size={20} />
        </button>
      </div>

      <Button 
        variant="secondary"
        size="lg"
        onClick={handleCompleteTask}
        className="w-full max-w-xs border-green-500 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 mb-8"
      >
        <CheckCircle size={20} className="mr-2" />
        Complete Task
      </Button>

      <div className="w-full max-w-md bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-4 shadow-sm animate-fade-in-up">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            <Volume2 size={16} className="text-blue-500" />
            <span>Soundscapes</span>
          </div>
          
          {selectedSoundId && (
            <div className="flex items-center gap-2 w-32">
              <VolumeX size={14} className="text-gray-400 cursor-pointer" onClick={() => setVolume(0)} />
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1" 
                value={volume} 
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <Volume2 size={14} className="text-gray-400" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {SOUNDSCAPES.map((sound) => {
            const isSelected = selectedSoundId === sound.id;
            const isLocked = user.level < (sound.minLevel || 1);

            return (
              <button
                key={sound.id}
                disabled={isLocked}
                onClick={() => handleSelectSound(sound.id)}
                className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-lg border transition-all relative overflow-hidden ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                    : isLocked
                        ? 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 opacity-60 cursor-not-allowed'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                }`}
              >
                <div className={`${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
                  <SoundIcon name={sound.icon} size={20} />
                </div>
                <span className="text-[10px] font-medium">{sound.label}</span>
                {isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100/50 dark:bg-gray-900/50">
                        <Lock size={12} className="text-gray-400" />
                    </div>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  );
};
